import { useEffect, useState } from "react";
import { CheckCircle, ListTodo, Mail, MessageSquare } from "lucide-react";
import { ComingSoon } from "pages/patient/components/ComingSoon/ComingSoon";

import { AddTaskModal } from "components/AddTaskModal/AddTaskModal";
import { SendEmailModal } from "components/Modal/SendEmailModal";
import { DataTable } from "components/ui/DataTable/DataTable";
import { Dialog } from "components/ui/Dialog";
import { SmsModal2 } from "features/comunications/components/SmsModal2/SmsModal2";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useConfirmCommonResult } from "features/results/hooks/useConfirmCommonResult";
import { useConfirmRadiologyResult } from "features/results/hooks/useConfirmRadiologyResult";
import { useConfirmResult } from "features/results/hooks/useConfirmResult";
import { isResultUnassigned } from "features/results/utils/resultAssignment";
import { isSupersededResult } from "features/results/utils/resultCorrection";
import { useDebounce } from "hooks/useDebounce";

import { CommonResultDetails } from "../../components/CommonResultDetails/CommonResultDetails";
import { HideReviewedToggle } from "../../components/HideReviewedToggle/HideReviewedToggle";
import { RadiologyResultDetails } from "../../components/RadiologyResultDetails/RadiologyResultDetails";
import { ResultDetails } from "../../components/ResultDetails/ResultDetails";
import {
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from "../../constants/pagination";
import { useNavigateToPatient } from "../../hooks/useNavigateToPatient";
import { useResultsSelection } from "../../hooks/useResultsSelection";
import { useStaffResultsData } from "../../hooks/useStaffResultsData";
import { getPatientName } from "../../utils/formatters";
import { getInitialAssigneeComments } from "../../utils/getInitialAssigneeComments";
import columnStyles from "./columns.module.css";
import { getCommonStaffColumns } from "./commonColumns";
import { getLaboratoryStaffColumns } from "./laboratoryColumns";
import { getRadiologyStaffColumns } from "./radiologyColumns";
import styles from "./StaffResultsTable.module.css";

const SORT_OPTIONS = [
  { field: "newest-first", direction: "", label: "Newest first" },
  { field: "oldest-first", direction: "", label: "Oldest first" },
];

export const StaffResultsTable = ({
  type,
  messageTypes,
  unassigned,
  tenantVal,
  disabled = false,
  onSelectItem,
  selectedItemId,
  onResultsLoaded,
}) => {
  const [sort, setSort] = useState({ field: "newest-first", direction: "" });
  const [search, setSearch] = useState("");
  const [hideReviewed, setHideReviewed] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [detailItem, setDetailItem] = useState(null);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [addTaskRows, setAddTaskRows] = useState([]);
  const [isSmsOpen, setIsSmsOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  // Reset the search when switching tab/type so a query doesn't carry over.
  useEffect(() => {
    setSearch("");
  }, [type, messageTypes]);

  const { navigateToPatient } = useNavigateToPatient();
  const { data: staffMember } = useStaffInfo();

  const { mutate: confirmLab } = useConfirmResult();
  const { mutate: confirmRad } = useConfirmRadiologyResult();
  const { mutate: confirmCommon } = useConfirmCommonResult();
  const confirm =
    type === "radiology"
      ? confirmRad
      : type === "common"
      ? confirmCommon
      : confirmLab;

  // Server-side search (BE requires >= 3 chars) for every type.
  const debouncedSearch = useDebounce(search, 400);
  const trimmedSearch = debouncedSearch.trim();
  const searchQuery = trimmedSearch.length >= 3 ? trimmedSearch : undefined;

  // "Hide reviewed" is now a server-side filter (unreviewed=true → non-CONFIRMED).
  const unreviewed = hideReviewed ? true : undefined;

  // Reset to the first page whenever the query changes, so we never sit on an
  // out-of-range page after the result set shrinks.
  useEffect(() => {
    setPage(1);
  }, [type, messageTypes, searchQuery, unassigned, unreviewed, sort.field]);

  const { results, totalCount, serverPaginated, isLoading, isError, error } =
    useStaffResultsData({
      type,
      messageTypes,
      searchQuery,
      unassigned,
      unreviewed,
      limit: rowsPerPage,
      offset: (page - 1) * rowsPerPage,
      orderBy: sort.field === "newest-first" ? "DESC" : "ASC",
      enabled: !disabled,
    });

  // Filtering, search and pagination are all server-side now (BE narrows by
  // messageTypes and returns the requested page + totalCount).
  const filteredData = results;

  /*
   * The page the table is showing, handed up so the detail beside it can resolve a correction's
   * counterpart (KIN-2089). The table owns the rows; the panel that links to one of them does
   * not, and there is no by-inbox-id fetch to ask the backend for a single result.
   */
  useEffect(() => {
    onResultsLoaded?.(results);
  }, [results, onResultsLoaded]);

  const { selection, clearSelection } = useResultsSelection({
    filteredData,
    getItemId: (row) => row.id,
  });

  const columns =
    type === "laboratory"
      ? getLaboratoryStaffColumns({ navigateToPatient })
      : type === "radiology"
      ? getRadiologyStaffColumns({ navigateToPatient })
      : getCommonStaffColumns({ navigateToPatient });

  const selectedRows = filteredData.filter((row) =>
    selection.isSelected(row.id)
  );
  const hasUnconfirmed = selectedRows.some(
    (r) => r.orderStatus !== "CONFIRMED"
  );

  const bulkActions = [
    {
      label: "Add task",
      onClick: (rows) => {
        setAddTaskRows(rows);
        setIsAddTaskOpen(true);
      },
    },
    {
      label: "Send SMS",
      onClick: () => setIsSmsOpen(true),
    },
    ...(hasUnconfirmed
      ? [
          {
            label: "Mark as reviewed",
            onClick: (rows) =>
              confirm({
                ids: rows
                  .filter((r) => r.orderStatus !== "CONFIRMED")
                  .map((r) => r.id),
              }),
          },
        ]
      : []),
  ];

  const handleOpenDetail = (row) => {
    if (onSelectItem) {
      onSelectItem(row);
    } else {
      setDetailItem(row);
    }
  };

  const rowActions = [
    {
      label: "Add task",
      icon: ListTodo,
      onClick: (row) => {
        setAddTaskRows([row]);
        setIsAddTaskOpen(true);
      },
      visibleWhen: (row) => !!row.patient,
    },
    {
      label: "Mark as reviewed",
      icon: CheckCircle,
      onClick: (row) => confirm({ ids: [row.id] }),
      visibleWhen: (row) => row.orderStatus !== "CONFIRMED",
    },
    {
      label: "Send SMS",
      icon: MessageSquare,
      onClick: (row) => {
        setActiveRow(row);
        setIsSmsOpen(true);
      },
      visibleWhen: (row) => !!row.patient,
    },
    {
      label: "Send Email",
      icon: Mail,
      onClick: (row) => {
        setActiveRow(row);
        setIsEmailOpen(true);
      },
      visibleWhen: (row) => !!row.patient,
    },
  ];

  if (disabled) return <ComingSoon />;

  return (
    <>
      <DataTable
        columns={columns}
        data={filteredData}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        totalCount={serverPaginated ? totalCount : undefined}
        rowsOptions={ROWS_PER_PAGE_OPTIONS}
        rowsValue={rowsPerPage}
        onRowsChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
        pagination={{ page, onPageChange: setPage }}
        onRowClick={handleOpenDetail}
        getRowClassName={(row) => {
          /* A result the lab has replaced is struck through wherever it is listed (KIN-2089).
             It composes with the others rather than replacing them: a superseded row can still
             be the selected one, and can still be unassigned. */
          const superseded = isSupersededResult(row)
            ? columnStyles.supersededRow
            : undefined;

          if (row.id === selectedItemId)
            return [styles.selectedRow, superseded].filter(Boolean).join(" ");

          return (
            [isResultUnassigned(row) ? "warning" : undefined, superseded]
              .filter(Boolean)
              .join(" ") || undefined
          );
        }}
        emptyTitle={isError ? "Error loading results" : "No items found"}
        emptyText={
          isError
            ? error?.response?.status === 403
              ? "You don't have permission to view these results."
              : "Unable to load results. Please try again later."
            : search
            ? "Try adjusting your search to find what you're looking for."
            : "There are no items to display at this time."
        }
        search={{
          value: search,
          onChange: (v) => {
            setSearch(v);
            clearSelection();
          },
          placeholder: "Search",
          maxWidth: "210px",
        }}
        sort={sort}
        sortOptions={SORT_OPTIONS}
        onSortChange={(field, direction) => setSort({ field, direction })}
        toolbarExtra={
          <HideReviewedToggle value={hideReviewed} onChange={setHideReviewed} />
        }
        selectable
        selection={selection}
        rowActions={rowActions}
        bulkActions={bulkActions}
        actionsColumnWidth="32px"
      />

      {!onSelectItem && detailItem && (
        <Dialog
          open
          title={
            <>
              {getPatientName(detailItem)}
              {detailItem.patientId && (
                <button
                  className={styles.openProfileButton}
                  onClick={(e) =>
                    navigateToPatient(
                      e,
                      detailItem.patientId,
                      getPatientName(detailItem)
                    )
                  }
                >
                  Open profile
                </button>
              )}
            </>
          }
          onClose={() => setDetailItem(null)}
          width={940}
        >
          {type === "radiology" && <RadiologyResultDetails item={detailItem} />}
          {type === "common" && <CommonResultDetails item={detailItem} />}
          {type === "laboratory" && (
            <ResultDetails item={detailItem} staffMember={staffMember} />
          )}
        </Dialog>
      )}

      <AddTaskModal
        patient={addTaskRows?.at(0)?.patient}
        isOpen={isAddTaskOpen}
        setIsOpen={setIsAddTaskOpen}
        noButton
        staffMemberId={staffMember?.staffMember?.id?.toString()}
        initialAssigneeComments={getInitialAssigneeComments(addTaskRows, type)}
      />

      <Dialog
        open={isSmsOpen}
        onClose={() => {
          setIsSmsOpen(false);
          setActiveRow(null);
        }}
        title="Send SMS"
        width={1000}
      >
        <SmsModal2
          tenantVal={tenantVal}
          patient={activeRow?.patient ?? selectedRows?.at(0)?.patient}
          onClose={() => {
            setIsSmsOpen(false);
            setActiveRow(null);
          }}
        />
      </Dialog>

      <SendEmailModal
        isOpen={isEmailOpen}
        onClose={() => {
          setIsEmailOpen(false);
          setActiveRow(null);
        }}
        patient={activeRow?.patient}
      />
    </>
  );
};
